import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, registerAuthHandlers } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, obj=auth
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Register the global 401 handler once. On any unauthenticated API call,
  // clear local user state, preserve the current route as `?next=`, and
  // redirect to /login with a session-expired toast.
  useEffect(() => {
    registerAuthHandlers(() => {
      const currentPath = window.location.pathname + window.location.search;
      // Only fire once per expiry cycle — setUser(false) is idempotent, but
      // the toast + redirect shouldn't flood if several in-flight requests
      // all 401 together.
      setUser((prev) => (prev === false ? prev : false));
      // Skip when already on /login or a public page.
      if (
        window.location.pathname.startsWith("/login") ||
        window.location.pathname.startsWith("/audit")
      ) {
        return;
      }
      toast.error("Session expired — please log in again", {
        id: "session-expired",  // dedupe across simultaneous 401s
      });
      navigate(`/login?next=${encodeURIComponent(currentPath)}`,
               { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (e) {
        setUser(false);
      }
    })();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", {
        email, password, remember_me: rememberMe,
      });
      // Auth is carried by the httpOnly cookie set by the server.
      // We intentionally do NOT persist any token in localStorage (XSS-hardening).
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    // Clean up any legacy token left over from older builds.
    localStorage.removeItem("accp_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, error }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
